"use client";

import { format } from "date-fns";
import { motion } from "framer-motion";
import { FileText, MoreVertical, Pencil, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useJourneyData } from "@/hooks/use-journey-data";
import type { MortgageQuote } from "@/schemas/mortgage";

interface QuoteCardProps {
  quote: MortgageQuote;
  onEdit: (quoteId: string) => void;
}

export function QuoteCard({ quote, onEdit }: QuoteCardProps) {
  const { deleteMortgageQuote } = useJourneyData();

  const handleDelete = async () => {
    if (!quote.id) return;
    try {
      await deleteMortgageQuote.mutateAsync(quote.id);
    } catch (error) {
      console.error("Failed to delete quote:", error);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const getRateVariant = (
    rate: number,
  ): "default" | "destructive" | "outline" | "secondary" => {
    if (rate <= 5.5) return "secondary";
    if (rate <= 6.5) return "default";
    if (rate <= 7.5) return "outline";
    return "destructive";
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <Card className="group relative">
        <CardContent className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg">{quote.lender_name}</h3>
              <p className="text-sm text-muted-foreground">
                Added{" "}
                {format(
                  new Date(quote.created_at ?? new Date()),
                  "MMM d, yyyy",
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {quote.loan_estimate_document_id && (
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {quote.id && typeof quote.id === "string" && (
                    <DropdownMenuItem
                      onClick={() => onEdit(quote.id as string)}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={handleDelete}
                    className="text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Key Details */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm text-muted-foreground">Monthly Payment</p>
              <p className="text-lg font-semibold">
                {formatCurrency(quote.monthly_payment)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Loan Amount</p>
              <p className="text-lg font-semibold">
                {formatCurrency(quote.loan_amount)}
              </p>
            </div>
          </div>

          {/* Rate Information */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge
              variant={getRateVariant(quote.interest_rate)}
              className={
                quote.interest_rate <= 5.5
                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
                  : quote.interest_rate <= 6.5
                    ? ""
                    : quote.interest_rate <= 7.5
                      ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100"
                      : ""
              }
            >
              {quote.interest_rate}% Rate
            </Badge>
            {quote.apr && <Badge variant="outline">{quote.apr}% APR</Badge>}
          </div>

          {/* Additional Details */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">
              {quote.loan_type.replace("_", " ").toUpperCase()}
            </Badge>
            <Badge variant="secondary">{quote.loan_term_years} Year</Badge>
            {quote.points_cost && (
              <Badge variant="secondary">{quote.points_cost} Points</Badge>
            )}
            {quote.prepayment_penalty && (
              <Badge variant="outline">Prepayment Penalty</Badge>
            )}
          </div>

          {/* Closing Costs Summary */}
          {quote.total_closing_costs && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Total Closing Costs
              </p>
              <p className="font-semibold">
                {formatCurrency(quote.total_closing_costs)}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
